import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Workflow,
  ShieldCheck,
  Image as ImageIcon,
  Loader2,
  Printer,
  Sparkles,
  Building2,
  User,
  ListChecks,
  Bot,
  ExternalLink,
  Pencil,
  Copy,
  Check,
  Plug,
  Wand2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import InviktusLogo from "@/components/InviktusLogo";
import {
  finalizeSession,
  getReport,
  excelUrl,
  chartUrl,
  chartPdfUrl,
  generateAgents,
  listSessionAgents,
  agentJsonUrl,
  agentMarkdownUrl,
  agentPdfUrl,
} from "@/lib/api";

export default function ExecutiveReport() {
  const { sid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Fix 6: re-generating replaces existing agents; the server only allows it to staff.
  const canRegenerate = ["admin", "consultor"].includes(user?.role);
  const [report, setReport] = useState(null);
  const [reportLlm, setReportLlm] = useState(null); // fix 12: model that produced the report
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [generatingAgents, setGeneratingAgents] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState(null);
  // 409 company_required / 403 company_inactive from "Generar agentes" (step 4a contract)
  const [generateNotice, setGenerateNotice] = useState(null);

  const loadAgents = async () => {
    try {
      setAgentsLoading(true);
      const data = await listSessionAgents(sid);
      setAgents(data.agents || []);
    } catch {
      // silent
    } finally {
      setAgentsLoading(false);
    }
  };

  const onGenerateAgents = async () => {
    try {
      setGeneratingAgents(true);
      setGenerateNotice(null);
      toast.success("Generando agentes IA por departamento. Tomará 30-90 segundos.");
      const data = await generateAgents(sid);
      setAgents((data.agents || []).map((x) => ({ ...x, can_export: x.can_export ?? !!x.demo })));
      loadAgents(); // refresh: the list carries `can_export` for this visitor (step 5b)
      toast.success(`${(data.agents || []).length} agente(s) generado(s).`);
    } catch (e) {
      const st = e?.response?.status;
      const code = e?.response?.data?.code;
      if ((st === 409 && code === "company_required") || (st === 403 && code === "company_inactive")) {
        setGenerateNotice({
          code,
          message:
            e.response.data?.detail ||
            "Para generar agentes, esta entrevista debe estar asociada a una empresa. Solicite a Inviktus el enlace de su empresa.",
        });
        toast.dismiss();
        return;
      }
      if (st === 429) {
        toast.error(e?.response?.data?.detail || "Demasiadas solicitudes. Intente de nuevo más tarde.");
        return;
      }
      if (code === "regenerate_login_required" || code === "regenerate_staff_only") {
        toast.error(e.response.data?.detail || "Solo el equipo de Inviktus puede regenerar los agentes.");
        return;
      }
      console.error(e);
      // The server may have persisted them even if the proxy timed out
      await loadAgents();
      toast.error("La generación tardó más de lo esperado. Revise abajo si los agentes ya están creados.");
    } finally {
      setGeneratingAgents(false);
    }
  };

  const copyAgentLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(window.location.origin + `/agentes/${slug}`);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 1500);
    } catch {
      toast.error("No se pudo copiar.");
    }
  };

  const load = async () => {
    // Try existing report → if absent, request finalize with retries.
    // The synchronous LLM extraction can exceed the K8s ingress timeout for
    // rich multi-person sessions, so we retry a few times before giving up.
    try {
      const data = await getReport(sid);
      setReport(data.report); setReportLlm(data.report_llm || null);
      setLoading(false);
      return;
    } catch {
      // continue to finalize path
    }
    setGenerating(true);
    const maxAttempts = 4;
    let waits = 0; // Fix 7 (A3): 409 report_in_progress = another request is extracting it
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const data = await finalizeSession(sid);
        setReport(data.report); setReportLlm(data.report_llm || null);
        setGenerating(false);
        setLoading(false);
        return;
      } catch (err) {
        // proxy may 502 on long extractions, but report often persists. Poll.
        if (err?.response?.data?.code === "report_in_progress" && waits < 45) {
          waits += 1;
          attempt -= 1; // waiting for the running extraction does not use up an attempt (max ~3 min)
        }
        try {
          await new Promise((r) => setTimeout(r, 4000));
          const d2 = await getReport(sid);
          if (d2?.report) {
            setReport(d2.report); setReportLlm(d2.report_llm || null);
            setGenerating(false);
            setLoading(false);
            return;
          }
        } catch {
          // not ready yet
        }
        if (attempt === maxAttempts) {
          console.error(err);
          toast.error("No se pudo cargar el informe. Vuelva a la entrevista.");
        }
      }
    }
    setGenerating(false);
    setLoading(false);
  };

  useEffect(() => {
    if (sid) {
      load();
      loadAgents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  if (loading || generating) {
    return (
      <div className="flex h-screen items-center justify-center bg-white text-slate-700">
        <div className="text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-900" />
          <p className="mt-4 text-xs uppercase tracking-[0.22em] text-slate-500">
            {generating ? "Generando informe ejecutivo..." : "Cargando informe..."}
          </p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-white text-slate-700">
        <p className="font-display text-2xl text-slate-900">Informe no disponible</p>
        <Button
          onClick={() => navigate(`/consulta?session=${sid}`)}
          className="mt-6 rounded-sm bg-slate-900 text-xs uppercase tracking-[0.2em] text-white hover:bg-slate-800"
        >
          Volver a la entrevista
        </Button>
      </div>
    );
  }

  const departments = report.departamentos || [];
  const allActivities = departments.flatMap((d) =>
    (d.personas || []).flatMap((p) => p.actividades || [])
  );
  const nDept = departments.length;
  const nPeople = departments.reduce((s, d) => s + (d.personas || []).length, 0);
  const nActs = allActivities.length;

  // Expected activity count per department (used to flag out-of-sync agents)
  const expectedActivitiesByDept = {};
  for (const d of departments) {
    const count = (d.personas || []).reduce(
      (s, p) => s + ((p.actividades || []).length || 0),
      0,
    );
    expectedActivitiesByDept[(d.nombre || "").trim().toLowerCase()] = count;
  }

  const agentSyncStatus = (agent) => {
    const dept = (agent.department || "").trim().toLowerCase();
    const expected = expectedActivitiesByDept[dept] ?? null;
    const knows = (agent.prompts || []).length;
    const hasFlow = (agent.abc_flow_chars || 0) > 0;
    if (expected == null) return { ok: false, expected, knows, hasFlow, label: "Departamento sin datos" };
    if (!hasFlow) return { ok: false, expected, knows, hasFlow, label: "Sin flujo ABC inyectado · regenerar" };
    if (knows !== expected) return { ok: false, expected, knows, hasFlow, label: `Desactualizado · ${knows}/${expected} actividades` };
    return { ok: true, expected, knows, hasFlow, label: `Sincronizado · ${knows}/${expected} actividades` };
  };

  const anyOutOfSync = agents.some((a) => !agentSyncStatus(a).ok);

  const e = report.estrategia || {};
  const m = report.metricas || {};

  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="executive-report">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur print:hidden">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4 lg:px-10">
          <div className="flex items-center gap-6">
            <button
              onClick={() => navigate(`/consulta?session=${sid}`)}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900"
              data-testid="back-to-chat-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Entrevista
            </button>
            <Separator orientation="vertical" className="hidden h-6 bg-slate-300 sm:block" />
            <InviktusLogo className="text-slate-900 hidden shrink-0 sm:inline-flex" size={22} />
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ThemeToggle />
            <a href="/dashboard" data-testid="nav-dashboard-from-report">
              <Button
                variant="outline"
                className="rounded-sm border-slate-300 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
              >
                Dashboard
              </Button>
            </a>
            <Button
              onClick={onPrint}
              variant="outline"
              className="rounded-sm border-slate-300 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
              data-testid="print-report-btn"
            >
              <Printer className="mr-2 h-4 w-4" /> Imprimir
            </Button>
            <Link to={`/informe/${sid}/flujo`} data-testid="open-flow-document-btn">
              <Button
                variant="outline"
                className="rounded-sm border-slate-900 bg-white text-xs uppercase tracking-[0.2em] text-slate-900 hover:bg-slate-100"
              >
                <Workflow className="mr-2 h-4 w-4" /> Ver flujo
              </Button>
            </Link>
            <a
              href={chartUrl(sid)}
              target="_blank"
              rel="noreferrer"
              download
              data-testid="download-chart-btn"
            >
              <Button
                variant="outline"
                className="rounded-sm border-slate-900 bg-white text-xs uppercase tracking-[0.2em] text-slate-900 hover:bg-slate-100"
              >
                <ImageIcon className="mr-2 h-4 w-4" /> Flujo PNG
              </Button>
            </a>
            <a
              href={chartPdfUrl(sid)}
              target="_blank"
              rel="noreferrer"
              download
              data-testid="download-chart-pdf-btn"
            >
              <Button
                variant="outline"
                className="rounded-sm border-slate-900 bg-white text-xs uppercase tracking-[0.2em] text-slate-900 hover:bg-slate-100"
              >
                <Download className="mr-2 h-4 w-4" /> Flujo PDF
              </Button>
            </a>
            <a href={excelUrl(sid)} download data-testid="download-excel-btn">
              <Button className="rounded-sm bg-slate-900 text-xs uppercase tracking-[0.2em] text-white hover:bg-slate-800">
                <Download className="mr-2 h-4 w-4" /> Descargar Excel
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 lg:px-10">
        {/* Title block */}
        <div className="border-b border-slate-300 pb-10">
          <p className="eyebrow text-accent-blue">Inviktus · Informe Ejecutivo</p>
          <h1 className="mt-5 font-display text-4xl font-medium leading-tight text-slate-900 sm:text-5xl">
            Diagnóstico Activity-Based Costing
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700">
            {report.empresa_contexto || "Resumen del proceso o área analizada en esta sesión."}
          </p>
          <div className="mt-8 grid grid-cols-2 gap-6 text-xs uppercase tracking-[0.2em] text-slate-500 sm:grid-cols-4">
            <KpiMeta icon={Building2} label="Departamentos" value={String(nDept)} />
            <KpiMeta icon={User} label="Personas / Cargos" value={String(nPeople)} />
            <KpiMeta icon={ListChecks} label="Actividades" value={String(nActs)} />
            {/* fix 12: real model; reports made before fix 12 used Claude Sonnet 4.5 */}
            <Meta label="Modelo" value={reportLlm?.model || "Claude Sonnet 4.5"} />
          </div>
        </div>

        {/* Recommendations */}
        <section className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow">Síntesis</p>
            <h2 className="mt-3 font-display text-3xl font-medium text-slate-900">
              Estructura mapeada
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
              El diagnóstico ABC se construye de forma jerárquica: cada actividad pertenece a una
              persona, y los pesos % de las actividades suman <strong>100% por persona</strong>, no
              globalmente. A continuación, un desglose por departamento, persona y actividad.
            </p>

            <div className="mt-6 border border-slate-200 bg-white p-7">
              <p className="eyebrow">Marco estratégico</p>
              <dl className="mt-5 grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-8">
                <KV k="¿Tiene estrategia?" v={e.tiene_estrategia} />
                <KV k="Modelo IA preferido" v={e.modelo_ia_preferido} />
                <KV k="Objetivos generales" v={e.objetivos_generales} colspan />
                <KV k="Métricas" v={(m.lista || []).join(", ")} colspan />
                <KV k="Objetivos numéricos" v={m.objetivos_numericos} />
                <KV k="Tiempos" v={m.tiempos} />
                <KV k="Frecuencia verificación" v={m.frecuencia_verificacion} />
                <KV k="Presupuesto tokens/mes" v={m.presupuesto_tokens_mes} />
                <KV k="Supervisor (verificar)" v={m.supervisor_verificar} />
                <KV k="Supervisor (aceptar)" v={m.supervisor_aceptar} />
              </dl>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="border border-slate-200 bg-white p-7">
              <p className="eyebrow text-accent-blue">Recomendaciones</p>
              <h3 className="mt-3 flex items-center gap-2 font-display text-xl font-medium text-slate-900">
                <Sparkles className="h-4 w-4 text-accent-blue" /> Del consultor senior
              </h3>
              <ol className="mt-5 space-y-4 text-sm leading-relaxed text-slate-700">
                {(report.recomendaciones_consultor || []).map((r, i) => (
                  <li key={`${r.substring(0, 40)}-${i}`} className="flex gap-3">
                    <span className="mt-0.5 font-mono text-xs text-slate-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{r}</span>
                  </li>
                ))}
                {(!report.recomendaciones_consultor || report.recomendaciones_consultor.length === 0) && (
                  <li className="text-slate-500">Sin recomendaciones registradas.</li>
                )}
              </ol>
            </div>
          </aside>
        </section>

        {/* Departments */}
        <section className="mt-16 space-y-14">
          {departments.map((dept, di) => (
            <DepartmentBlock key={dept.nombre || `dept-${di}`} dept={dept} index={di} />
          ))}
          {departments.length === 0 && (
            <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
              No se mapeó ningún departamento todavía. Vuelva a la entrevista para completar el diagnóstico.
            </div>
          )}
        </section>

        {/* Generated AI Agents per Department */}
        <section className="mt-20 border-t border-slate-300 pt-12" data-testid="agents-section">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow text-accent-blue">Activación</p>
              <h2 className="mt-3 font-display text-3xl font-medium text-slate-900">
                Agentes IA por departamento
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                A partir del diagnóstico ABC podemos generar un agente IA específico por cada
                departamento. Cada agente recibe automáticamente el <strong>flujo completo del
                proceso</strong> (personas, actividades con %, micro-tareas en orden, herramientas
                y destinos) como parte de su instrucción de sistema, además de los iniciadores de
                conversación por cada actividad.
              </p>
            </div>
            {(agents.length === 0 || canRegenerate) && (
            <Button
              onClick={onGenerateAgents}
              disabled={generatingAgents || departments.length === 0}
              className="rounded-sm bg-accent-blue text-xs uppercase tracking-[0.2em] text-white hover:bg-accent-blue-light"
              data-testid="generate-agents-btn"
            >
              {generatingAgents ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-2 h-4 w-4" />
              )}
              {agents.length > 0 ? "Regenerar desde informe" : "Generar agentes"}
            </Button>
            )}
          </div>

          {generateNotice && (
            <div
              className="mt-6 flex items-start gap-3 border border-amber-500/60 bg-amber-50 p-4 text-sm text-amber-900"
              data-testid="generate-agents-notice"
            >
              <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">
                  {generateNotice.code === "company_required"
                    ? "Esta entrevista no está asociada a ninguna empresa."
                    : "La empresa de esta entrevista está desactivada."}
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-amber-900/80" data-testid="generate-agents-notice-message">
                  {generateNotice.message}
                </p>
                <p className="mt-1 text-[12px] text-amber-900/70">
                  El informe sigue disponible y puede descargarlo con normalidad.
                </p>
              </div>
              <button
                onClick={() => setGenerateNotice(null)}
                className="text-[11px] uppercase tracking-[0.16em] text-amber-800 hover:underline"
                data-testid="generate-agents-notice-close"
              >
                Cerrar
              </button>
            </div>
          )}

          {anyOutOfSync && agents.length > 0 && canRegenerate && (
            <div
              className="mt-6 flex flex-wrap items-start gap-3 border border-amber-500/60 bg-amber-50 p-4 text-sm text-amber-900"
              data-testid="agents-out-of-sync-banner"
            >
              <span className="mt-0.5 text-base">⚠</span>
              <div className="flex-1">
                <p className="font-medium">
                  Uno o más agentes están desactualizados respecto al informe actual.
                </p>
                <p className="mt-1 text-[13px] leading-relaxed text-amber-900/80">
                  Esto suele ocurrir cuando se modificó el informe (más actividades, micro-tareas
                  o herramientas) después de generar los agentes. Use <strong>Regenerar desde
                  informe</strong> para que cada agente conozca el flujo completo y los
                  iniciadores estén alineados con las {nActs} actividad{nActs === 1 ? "" : "es"} actuales.
                </p>
              </div>
              <Button
                onClick={onGenerateAgents}
                disabled={generatingAgents}
                variant="outline"
                className="rounded-sm border-amber-500 bg-white text-[11px] uppercase tracking-[0.18em] text-amber-800 hover:bg-amber-100"
                data-testid="regenerate-agents-banner-btn"
              >
                {generatingAgents ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wand2 className="mr-2 h-3.5 w-3.5" />
                )}
                Regenerar ahora
              </Button>
            </div>
          )}

          {agentsLoading && agents.length === 0 && (
            <div className="mt-10 flex items-center gap-3 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando agentes…
            </div>
          )}

          {!agentsLoading && agents.length === 0 && !generatingAgents && (
            <div className="mt-8 border border-dashed border-slate-300 bg-white p-8">
              <p className="text-sm text-slate-600">
                Aún no se han generado agentes para esta sesión. Haga clic en{" "}
                <strong>“Generar agentes”</strong> para crear uno por cada departamento mapeado.
              </p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-amber-700">
                Nota: la conexión real a herramientas vía MCP estará marcada como{" "}
                <span className="font-mono">MOCKED</span> en este prototipo.
              </p>
            </div>
          )}

          {agents.length > 0 && (
            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="agents-grid">
              {agents.map((a) => {
                const enabledTools = (a.tools || []).filter((t) => t.enabled);
                const publicUrl = `${window.location.origin}/agentes/${a.slug}`;
                const sync = agentSyncStatus(a);
                return (
                  <article
                    key={a.slug}
                    className="border border-slate-200 bg-white p-7"
                    data-testid={`agent-card-${a.slug}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center bg-slate-900 text-accent-blue-light">
                          <Bot className="h-5 w-5" strokeWidth={1.8} />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-[0.22em] text-accent-blue">
                            {a.department}
                          </p>
                          <h3 className="mt-1 font-display text-xl font-medium text-slate-900">
                            {a.name}
                          </h3>
                        </div>
                      </div>
                      <span
                        className={`rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${
                          sync.ok
                            ? "border-teal-700/50 bg-teal-50 text-teal-800"
                            : "border-amber-500/60 bg-amber-50 text-amber-800"
                        }`}
                        title={
                          sync.hasFlow
                            ? `Flujo ABC inyectado · ${(a.abc_flow_chars || 0).toLocaleString("es-419")} caracteres de contexto`
                            : "Este agente fue generado antes de la inyección automática del flujo ABC. Regenere para que conozca todo el proceso."
                        }
                        data-testid={`agent-sync-${a.slug}`}
                      >
                        {sync.ok ? "✓ " : "⚠ "}
                        {sync.label}
                      </span>
                    </div>

                    <p className="mt-5 text-sm leading-relaxed text-slate-700">
                      {a.description || "—"}
                    </p>

                    <div className="mt-5">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
                        <Plug className="h-3 w-3" /> Herramientas conectadas
                      </p>
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {enabledTools.length === 0 && (
                          <span className="text-[12px] italic text-slate-500">
                            Sin herramientas detectadas.
                          </span>
                        )}
                        {enabledTools.map((t) => (
                          <span
                            key={t.id}
                            className="border border-teal-700/40 bg-teal-50 px-2 py-0.5 text-[11px] uppercase tracking-[0.1em] text-teal-800"
                          >
                            {t.label}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6 border-t border-slate-200 pt-4">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        Enlace público (prototipo)
                      </p>
                      <div className="mt-2 flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2">
                        <code className="flex-1 truncate font-mono text-[11px] text-slate-700">
                          {publicUrl}
                        </code>
                        <button
                          onClick={() => copyAgentLink(a.slug)}
                          className="text-slate-500 hover:text-slate-900"
                          title="Copiar enlace"
                          data-testid={`copy-link-${a.slug}`}
                        >
                          {copiedSlug === a.slug ? (
                            <Check className="h-4 w-4 text-accent-blue" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <a
                        href={`/agentes/${a.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`open-agent-${a.slug}`}
                      >
                        <Button className="rounded-sm bg-slate-900 text-xs uppercase tracking-[0.2em] text-white hover:bg-slate-800">
                          <ExternalLink className="mr-2 h-4 w-4" /> Abrir agente
                        </Button>
                      </a>
                      <a href={`/agentes/${a.slug}/metricas`} data-testid={`metrics-agent-${a.slug}`}>
                        <Button
                          variant="outline"
                          className="rounded-sm border-slate-300 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
                        >
                          Métricas
                        </Button>
                      </a>
                      {user?.role === "admin" && <a href={`/agentes/${a.slug}/pruebas`} data-testid={`qa-agent-${a.slug}`}>
                        <Button
                          variant="outline"
                          className="rounded-sm border-slate-300 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
                        >
                          <ShieldCheck className="mr-2 h-4 w-4" /> Pruebas
                        </Button>
                      </a>}
                      {canRegenerate && ( /* Fix 7 (N17): editing is staff only */
                      <a href={`/agentes/${a.slug}/editar`} data-testid={`edit-agent-${a.slug}`}>
                        <Button
                          variant="outline"
                          className="rounded-sm border-slate-300 text-xs uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100"
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Editar
                        </Button>
                      </a>
                      )}
                    </div>

                    {/* step 5b: configuration downloads only when the backend says this visitor may export
                        (demo agents: always; real companies: staff, master or company users with access). */}
                    {a.can_export !== false ? (
                    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`export-section-${a.slug}`}>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                        Exportar configuración (para otra plataforma)
                      </p>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <a
                          href={agentJsonUrl(a.slug)}
                          download
                          data-testid={`export-json-${a.slug}`}
                          className="inline-flex items-center gap-1.5 border border-slate-300 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 hover:bg-slate-100"
                        >
                          <Download className="h-3 w-3" /> JSON
                        </a>
                        <a
                          href={agentMarkdownUrl(a.slug)}
                          download
                          data-testid={`export-md-${a.slug}`}
                          className="inline-flex items-center gap-1.5 border border-slate-300 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 hover:bg-slate-100"
                        >
                          <Download className="h-3 w-3" /> MARKDOWN
                        </a>
                        <a
                          href={agentPdfUrl(a.slug)}
                          download
                          data-testid={`export-pdf-${a.slug}`}
                          className="inline-flex items-center gap-1.5 border border-slate-300 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-700 hover:bg-slate-100"
                        >
                          <Download className="h-3 w-3" /> PDF
                        </a>
                      </div>
                    </div>
                    ) : (
                      <p
                        className="mt-4 border-t border-slate-100 pt-4 text-[12px] text-slate-500"
                        data-testid={`export-restricted-${a.slug}`}
                      >
                        La descarga de la configuración de este agente está disponible solo para el equipo de
                        Inviktus y para los usuarios de la empresa con acceso, con su sesión iniciada.
                      </p>
                    )}
                  </article>
                );
              })}
            </div>
          )}

          <p className="mt-8 text-[11px] uppercase tracking-[0.18em] text-amber-700" data-testid="mcp-mocked-notice">
            * Conexión MCP a herramientas (Email, SAP, CRM, BD, etc.) marcada como{" "}
            <span className="font-mono">MOCKED</span>. Se activará en producción con credenciales reales.
          </p>
        </section>

        <footer className="mt-20 border-t border-slate-300 pt-8 text-xs text-slate-500">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <span className="uppercase tracking-[0.22em]">
              Documento generado por Inviktus · Agente IA Senior ABC
            </span>
            <span className="font-mono">
              Sesión {sid} · {new Date().toLocaleString("es-419")}
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
}

function DepartmentBlock({ dept, index }) {
  const personas = dept.personas || [];
  return (
    <div data-testid={`department-block-${index}`}>
      <div className="flex items-baseline gap-4">
        <span className="font-mono text-xs text-slate-400">DPTO {String(index + 1).padStart(2, "0")}</span>
        <h2 className="font-display text-3xl font-medium text-slate-900">{dept.nombre || "—"}</h2>
        <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
          {personas.length} persona{personas.length !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="mt-2 h-px w-24 bg-accent-blue" />

      <div className="mt-10 space-y-12">
        {personas.map((p, pi) => (
          <PersonBlock key={p.nombre || p.cargo || `person-${pi}`} dept={dept.nombre} person={p} />
        ))}
        {personas.length === 0 && (
          <p className="text-sm italic text-slate-500">Sin personas mapeadas en este departamento.</p>
        )}
      </div>
    </div>
  );
}

function PersonBlock({ dept, person }) {
  const acts = (person.actividades || []).map((a) => ({
    ...a,
    peso: Number(a.peso) || 0,
  }));
  const sorted = [...acts].sort((a, b) => b.peso - a.peso);
  const total = acts.reduce((s, a) => s + (Number(a.peso) || 0), 0);
  const prof = person.perfil || {};
  const dec = person.decisiones || {};

  return (
    <article className="border border-slate-200 bg-white" data-testid="person-block">
      {/* header */}
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 bg-slate-50 px-7 py-6">
        <div>
          <p className="eyebrow text-accent-blue">{dept}</p>
          <h3 className="mt-2 font-display text-2xl font-medium text-slate-900">
            {person.cargo || "—"}{person.nombre ? <span className="text-slate-500"> · {person.nombre}</span> : null}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {prof.modalidad || ""} {prof.dias_trabajo ? ` · ${prof.dias_trabajo}` : ""}{" "}
            {prof.horario ? `· ${prof.horario}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-6 text-xs uppercase tracking-[0.18em] text-slate-500">
          <Pill label="Sustituir" value={dec.sustituir} />
          <Pill label="Agente virtual" value={dec.asignar_agente} />
          <Pill label="Promoción" value={dec.promocion_a} />
        </div>
      </div>

      {/* profile mini-grid */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-3 border-b border-slate-200 px-7 py-5 text-sm md:grid-cols-4">
        <Mini k="Jefe directo" v={prof.jefe_directo} />
        <Mini k="Modalidad" v={prof.modalidad} />
        <Mini k="Carrera" v={prof.carrera} />
        <Mini k="Exp. cargo" v={prof.anos_experiencia_cargo} />
      </div>

      {/* chart + table */}
      <div className="grid grid-cols-1 gap-0 lg:grid-cols-12">
        <div className="border-b border-slate-200 p-7 lg:col-span-6 lg:border-b-0 lg:border-r">
          <div className="flex items-baseline justify-between">
            <p className="eyebrow">Distribución de tiempo</p>
            <p className="font-mono text-xs text-slate-500">
              Total <span className="font-bold text-slate-900">{total.toFixed(1)}%</span>
            </p>
          </div>

          <div
            className="mt-5 h-[300px] w-full"
            style={{ height: Math.max(220, 44 * Math.max(sorted.length, 1) + 60) }}
            data-testid="person-activities-chart"
          >
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
              <BarChart
                data={sorted}
                layout="vertical"
                margin={{ top: 4, right: 56, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke="#E2E8F0" strokeDasharray="2 4" />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fill: "#64748B", fontSize: 11 }}
                  axisLine={{ stroke: "#E2E8F0" }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="nombre"
                  tick={{ fill: "#0F172A", fontSize: 11 }}
                  width={150}
                  axisLine={{ stroke: "#E2E8F0" }}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: "rgba(15,23,42,0.04)" }}
                  contentStyle={{
                    background: "#0F172A",
                    border: "none",
                    borderRadius: 2,
                    color: "#F8FAFC",
                    fontSize: 12,
                  }}
                  formatter={(v) => [`${Number(v).toFixed(1)}%`, "Peso"]}
                />
                <Bar dataKey="peso" barSize={18}>
                  {sorted.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#0D9488" : "#0F172A"} />
                  ))}
                  <LabelList
                    dataKey="peso"
                    position="right"
                    formatter={(v) => `${Number(v).toFixed(1)}%`}
                    style={{ fill: "#0F172A", fontSize: 11, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-7 lg:col-span-6">
          <p className="eyebrow">Detalle de actividades</p>
          <div className="mt-5">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-200">
                  <TableHead className="w-8 text-[11px] uppercase tracking-[0.18em] text-slate-500">#</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    Actividad
                  </TableHead>
                  <TableHead className="text-right text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    Peso
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    Importancia
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((a, i) => (
                  <TableRow key={a.nombre || `act-${i}`} className="border-slate-100 align-top">
                    <TableCell className="font-mono text-xs text-slate-500">{i + 1}</TableCell>
                    <TableCell>
                      <div className="font-display text-sm font-medium text-slate-900">{a.nombre}</div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {(a.herramientas || []).map((h, idx) => (
                          <span
                            key={`${h}-${idx}`}
                            className="border border-slate-300 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.1em] text-slate-700"
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                      {a.reporta_a && (
                        <div className="mt-1.5 text-[11px] text-slate-500">
                          Reporta a: {a.reporta_a}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold text-slate-900">
                      {Number(a.peso).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">
                      {a.importancia || "—"}
                      {a.complejidad && (
                        <div className="text-[11px] text-slate-500">Compl.: {a.complejidad}</div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-slate-200 bg-slate-50">
                  <TableCell />
                  <TableCell className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-bold text-slate-900">
                    {total.toFixed(1)}%
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </article>
  );
}

function KpiMeta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-accent-blue" strokeWidth={1.6} />
        <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
      </div>
      <p className="mt-2 font-display text-3xl font-medium text-slate-900">{value || "—"}</p>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
      <p className="mt-2 font-display text-sm text-slate-900">{value || "—"}</p>
    </div>
  );
}

function KV({ k, v, colspan }) {
  return (
    <div className={colspan ? "sm:col-span-2" : ""}>
      <dt className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
      <dd className="mt-1 text-sm text-slate-800">{v || "—"}</dd>
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</p>
      <p className="mt-1 truncate text-sm text-slate-800">{v || "—"}</p>
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <span className="mt-1 inline-flex max-w-[14rem] truncate font-display text-sm font-medium text-slate-900">
        {value || "—"}
      </span>
    </div>
  );
}
